import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award as AwardIcon } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, Spinner } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";
import { getAwards } from "@/lib/api";

export default function Awards() {
  const { data: awards = [], isLoading } = useQuery({ queryKey: ["awards"], queryFn: getAwards });
  const [year, setYear] = useState("All");
  const { tr } = useLanguage();

  const years = useMemo(() => ["All", ...Array.from(new Set(awards.map((a) => a.year))).sort().reverse()], [awards]);
  const filtered = year === "All" ? awards : awards.filter((a) => a.year === year);

  return (
    <div data-testid="awards-page">
      <PageSeo
        slug="awards"
        title="Awards & Recognition | Girish Kumar Naidu"
        description="Awards and recognitions received by Girish Kumar Naidu for sustained social service and community impact in Bengaluru."
        keywords="Girish Kumar Naidu awards, recognition, social service awards Bengaluru"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Awards & Recognition" }])]}
      />
      <PageHeader
        overline={tr("page.awards.overline")}
        title={tr("page.awards.title")}
        subtitle={tr("page.awards.subtitle")}
        crumbs={[{ name: "Awards & Recognition" }]}
      />

      <section className="bg-white py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="award-year-filters">
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                  year === y ? "bg-gold text-white" : "bg-paper border border-gray-200 text-gray-600 hover:border-gold hover:text-gold"
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          {isLoading ? (
            <Spinner />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((a, i) => (
                <Reveal key={a.id} delay={(i % 3) * 0.08}>
                  <div className="group bg-white rounded-2xl overflow-hidden border border-gray-200 card-lift h-full">
                    {a.image && (
                      <div className="h-48 overflow-hidden relative">
                        <img src={a.image} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <span className="absolute top-4 right-4 px-3 py-1 rounded-full font-mono text-sm font-bold bg-gold text-white">{a.year}</span>
                      </div>
                    )}
                    <div className="p-6">
                      <AwardIcon className="h-6 w-6 text-gold mb-3" />
                      <h3 className="font-display text-lg font-bold text-navy mb-1">{a.title}</h3>
                      <p className="font-mono text-[11px] uppercase tracking-wider text-civic mb-3">{a.organization}</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{a.description}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
